import React from "react";
import { useTranslation } from "react-i18next";
import { useModal } from "@/shared/utils/hooks/useModal";
import { InputField } from "@/shared/ui/InputField";
import { ModalFileName } from "@/shared/modals";
import { Row } from "@/shared/ui/Blocks";
import { ScreenContainer } from "@/shared/ui/ScreenContainer";
import { Title } from "@/shared/ui/Title";
import { IconButton } from "@/shared/ui/IconButton";
import { useSearchTalentPool } from "@/features/people/hooks/useSearchTalentPool";
import { MembersTable } from "@/shared/widgets/MembersTable";
import { FiltersDropdown } from "@/shared/widgets/FiltersDropdown";
import { useOrganisation } from "@/features/organization/hooks/useOrganisation";
import { RoleType, AccessLevel } from "@/shared/constants";
import { usePersonAccessLevelChange } from "@/features/people/hooks/usePersonAccessLevelChange";
import { usePersonRoleChange } from "@/features/people/hooks/usePersonRoleChange";
import { useRemovePersonFromOrganization } from "@/features/organization/hooks/useRemovePerson";
import { ResourceEnum } from "@/shared/constants/resources";
import { HeaderContent } from "@/shared/widgets";
import useViewerPermissions from "@/features/workspace/hooks/useViewerPermissions";
import { useAccount } from "@/features/workspace";
import { map } from "lodash";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";

export default function () {
  const { organization, loading } = useOrganisation();
  const {
    data,
    totalPages,
    limit,
    page,
    sort,
    handleSearch,
    filters,
    loading: loadingTable,
    handleFilterChange,
    clearFilter,
    handlePageChange,
    handleLimitChange,
    handleSortChange,
  } = useSearchTalentPool();

  const { getAccountAssessmentSurveys } = useAccount();

  const surveyConfig = map(
    getAccountAssessmentSurveys(),
    (s) => s?.type
  ) as AssessmentSurveyType[];

  const { t } = useTranslation();
  const { handleUpdatePersonAccessLevel } = usePersonAccessLevelChange();
  const { handleUpdatePersonRole } = usePersonRoleChange();
  const { validateAccessToResource, permissionsLoading } =
    useViewerPermissions();

  // Check if viewer can manage organization members
  const hasReadAccess = validateAccessToResource({
    resourceType: ResourceEnum.organization,
    accessLevel: AccessLevel.edit, // TODO: change to read when we have read access
  });

  const hasEditAccess = validateAccessToResource({
    resourceType: ResourceEnum.organization,
    accessLevel: AccessLevel.edit,
  });

  const [openAddOrgMembersModal] = useModal(ModalFileName.ADD_PEOPLE_V2, {
    resource: ResourceEnum.organization,
    resourceId: organization.id!,
    resourceName: organization.name || "",
    defaultRole: RoleType.member,
    defaultAccessLevel: AccessLevel.limited,
  });

  const handleSearchMember = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearch(e.target.value);
  };

  const handleAccessLevelChange = async (
    personId: number,
    access_level: string
  ) => {
    return await handleUpdatePersonAccessLevel({
      personId,
      access_level,
      objectType: "organization",
      objectId: organization.id!,
    });
  };

  // Only reachable for organization candidates being promoted to MEMBER — the table
  // keeps role editing disabled for every other role (see MembersTable).
  const handleRoleChange = async (personId: number, role: string) => {
    return await handleUpdatePersonRole({
      personId,
      role,
      objectType: "organization",
      objectId: organization.id!,
    });
  };

  const { removePersonFromOrganizationWithConfirm } =
    useRemovePersonFromOrganization();

  return (
    <>
      {hasEditAccess && (
        <HeaderContent>
          <IconButton
            icon="AddUser"
            label={t("organisation.invite_people")}
            variant="default"
            onClick={() => openAddOrgMembersModal()}
          />
        </HeaderContent>
      )}
      <ScreenContainer
        loading={loading || permissionsLoading}
        loadingMessage={t("common.loading_people")}
        className="overflow-y-auto relative"
        permissionLocked={!organization || !hasReadAccess}
      >
        <Row className="justify-between md:items-end mb-8 gap-4 flex-col md:flex-row">
          <Title
            // icon="Users"
            color="blue"
            description={organization?.name || ""}
          >
            {t("organisation.organisation_members")}
          </Title>
        </Row>

        <Row className="justify-between md:items-center flex-col md:flex-row gap-4 ">
          <Row className="md:w-[40%]">
            <InputField
              variant="ghost"
              size="small"
              deleteIcon
              icon="Search"
              type="text"
              name="search"
              onClearValue={() => handleSearch("")}
              placeholder={t("common.search")}
              autoComplete="off"
              onChange={handleSearchMember}
              wrapperClassName="border-b-grey-300 border-b border-solid"
            />
          </Row>
          <Row className="space-x-4 justify-between md:justify-normal">
            <FiltersDropdown
              label={t("common.status")}
              items={[
                {
                  id: "complete",
                  label: t("common.complete"),
                },
                {
                  id: "pending",
                  label: t("common.surveys_pending"),
                },
                {
                  id: "invited",
                  label: t("common.invited"),
                },
              ]}
              selected={filters.status ? [filters.status as string] : []}
              onSelect={(value) => handleFilterChange("status", value)}
              onClear={() => clearFilter("status")}
            />
            <FiltersDropdown
              label={t("common.role")}
              items={[
                {
                  id: RoleType.member,
                  label: t("common.member"),
                },
                {
                  id: RoleType.candidate,
                  label: t("common.candidate"),
                },
                {
                  id: RoleType.contact,
                  label: t("common.contact"),
                },
              ]}
              selected={filters.role ? [filters.role as string] : []}
              onSelect={(value) => handleFilterChange("role", value)}
              onClear={() => clearFilter("role")}
            />
            <FiltersDropdown
              label={t("people.access_level_column")}
              items={[
                {
                  id: AccessLevel.none,
                  label: t("common.access_level_none"),
                },
                {
                  id: AccessLevel.limited,
                  label: t("common.access_level_limited"),
                },
                {
                  id: AccessLevel.edit,
                  label: t("common.access_level_edit"),
                },
                {
                  id: AccessLevel.full,
                  label: t("common.access_level_full"),
                },
              ]}
              selected={
                filters.access_level ? [filters.access_level as string] : []
              }
              onSelect={(value) => handleFilterChange("access_level", value)}
              onClear={() => clearFilter("access_level")}
            />
          </Row>
        </Row>
        <MembersTable
          className="mt-4 -mr-4 md:mr-0"
          data={data}
          loading={loadingTable}
          pages={totalPages}
          currentPage={page}
          limit={limit}
          readOnly={!hasEditAccess}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          onSort={handleSortChange}
          disableRoleChange
          surveyConfig={surveyConfig}
          onRoleChange={handleRoleChange}
          onAccessLevelChange={handleAccessLevelChange}
          removePersonWithConfirm={removePersonFromOrganizationWithConfirm}
          sort={sort}
          tooltips={{
            access_level: t("organisation.members_access_level_tooltip"),
            role: t("organisation.members_role_tooltip"),
          }}
          resource={{
            type: ResourceEnum.organization,
            id: organization.id!,
          }}
        />
      </ScreenContainer>
    </>
  );
}
